/**
 * CompositePhenotypeCard — iter250 mass-market rewrite
 *
 * "Снимка на здравето ти днес"
 *
 * Mass-market UX changes (no backend changes):
 *   1. Renamed: "Вашият метаболитен фенотип" → "Снимка на здравето ти днес"
 *   2. Plain-language type label (mapped from backend type)
 *   3. New "Какво означава за теб" interpretation block
 *   4. Action CTA → /meal-intelligence
 *   5. Confidence: removed "Низка/Висока" wording, positive framing
 *   6. "Източници" → "Как го изчисляваме" (collapsed by default)
 *   7. Empty-state: 3-min assessment CTA when no data
 *
 * Backend untouched. Reads /api/me/phenotype-composite as before.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Info, ChevronDown, ChevronUp, ArrowRight, Activity } from "lucide-react";
import AIModuleNotice from "@/components/AIModuleNotice";
import WhyThisOutput from "@/components/WhyThisOutput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Plain-language mapping for backend phenotype keys ───
// Keys here mirror backend `primary_label_bg` strings — we map clinical phrasing
// into reader-friendly Bulgarian without mutating the underlying backend data.
const FRIENDLY_LABEL_BG = {
  "Инсулинова резистентност": "Тялото ти реагира по-бавно на захар",
  "Insulin resistance":       "Тялото ти реагира по-бавно на захар",
  "Метаболитен синдром":      "Натрупване на няколко метаболитни риска",
  "Хормонален дисбаланс":     "Хормоните ти не са в баланс в момента",
  "Хипотиреоидизъм":          "По-бавен метаболизъм (свързан с щитовидната)",
  "Хашимото":                 "Възпаление, свързано с щитовидната",
  "PCOS":                     "Поликистозни яйчници (PCOS)",
  "ПКЯС":                     "Поликистозни яйчници (СПКЯ)",
  "Преддиабет":               "Кръвната ти захар е по-висока от оптималното",
  "Затлъстяване":             "Тялото ти задържа повече тегло от необходимото",
  "Висцерална мазнина":       "Мазнини около вътрешните органи",
  "Възпалителен профил":      "Повишено възпаление в тялото",
  "Дислипидемия":             "Дисбаланс в нивата на холестерола",
};
const FRIENDLY_LABEL_EN = {
  "Insulin resistance":  "Your body responds slowly to sugar",
  "Metabolic syndrome":  "A cluster of metabolic risks",
  "Hormonal imbalance":  "Your hormones aren't in balance right now",
  "Hypothyroidism":      "Slower metabolism (thyroid-related)",
  "Hashimoto's":         "Inflammation related to the thyroid",
  "PCOS":                "Polycystic ovary syndrome (PCOS)",
  "Pre-diabetes":        "Your blood sugar is above optimal",
  "Obesity":             "Your body is holding more weight than needed",
  "Visceral fat":        "Fat around the internal organs",
  "Inflammatory profile": "Increased inflammation in the body",
  "Dyslipidemia":        "Cholesterol levels are out of balance",
};

// ─── "What this means for you" — short empathetic interpretations ───
const MEANING_BG = {
  "Инсулинова резистентност": "Често огладняваш по-бързо, имаш желание за сладко и отслабването е по-трудно. С подходящо хранене това може значително да се подобри.",
  "Insulin resistance":       "Често огладняваш по-бързо, имаш желание за сладко и отслабването е по-трудно. С подходящо хранене това може значително да се подобри.",
  "Метаболитен синдром":      "Няколко фактора (тегло, захар, налягане) действат заедно и натоварват организма. Малки ежедневни промени могат да окажат голям ефект.",
  "Хормонален дисбаланс":     "Енергията, настроението и апетитът ти могат да се променят без ясна причина. По-структуриран режим на хранене и сън помага да се стабилизират.",
  "Хипотиреоидизъм":          "Чувстваш се уморен, често ти е студено и теглото се сваля по-трудно. Балансираното хранене и добрият сън могат да помогнат.",
  "Хашимото":                 "Имунната система влияе на щитовидната жлеза. Намаляването на възпалението и стабилният дневен ритъм са важни.",
  "PCOS":                     "Често има връзка с начина, по който тялото обработва захарта. Балансираното хранене и контролът на въглехидратите могат да дадат резултат с времето.",
  "ПКЯС":                     "Често има връзка с начина, по който тялото обработва захарта. Балансираното хранене и контролът на въглехидратите могат да дадат резултат с времето.",
  "Преддиабет":               "Кръвната захар е по-висока от оптималното, но това често може да се подобри. С правилни навици можеш да върнеш баланса.",
  "Затлъстяване":              "Тялото задържа повече енергия, отколкото използва. Не само количеството, а и видът храна има ключово значение.",
  "Висцерална мазнина":       "Това е мазнината около вътрешните органи и влияе по-силно на здравето. Повлиява се най-много от храненето и инсулиновите пикове.",
  "Възпалителен профил":      "Възпалението в тялото може да влияе на енергията и възстановяването. Храната и сънят могат постепенно да помогнат да се намали.",
  "Дислипидемия":             "Балансът на холестерола е нарушен. Фибри, движение и качествени мазнини могат да помогнат за подобряване на стойностите.",
};
const MEANING_EN = {
  "Insulin resistance":   "You often feel hungry sooner, crave sweets and find weight loss harder. With the right nutrition this can improve significantly.",
  "Metabolic syndrome":   "Several factors (weight, sugar, pressure) work together and stress the body. Small daily changes can make a big difference.",
  "Hormonal imbalance":   "Energy, mood and appetite can shift without a clear reason. A more structured eating and sleep routine helps stabilise them.",
  "Hypothyroidism":       "You feel tired, often cold, and weight loss is slower. Balanced nutrition and good sleep can help.",
  "Hashimoto's":          "The immune system is affecting the thyroid. Reducing inflammation and keeping a steady daily rhythm matters.",
  "PCOS":                 "Often connected to how the body processes sugar. Balanced eating and carb control can deliver results over time.",
  "Pre-diabetes":         "Blood sugar is above optimal but this can usually improve. With the right habits you can restore balance.",
  "Obesity":              "The body is storing more energy than it uses. Not just quantity — the type of food matters too.",
  "Visceral fat":         "This is fat around the internal organs and has a stronger impact on health. It responds most to nutrition and lower insulin spikes.",
  "Inflammatory profile": "Inflammation in the body can affect energy and recovery. Food and sleep can gradually help reduce it.",
  "Dyslipidemia":         "Cholesterol balance is off. Fiber, movement and quality fats can help improve the values.",
};

function friendlyLabel(rawLabel, lang) {
  if (!rawLabel) return rawLabel;
  const map = lang === "bg" ? FRIENDLY_LABEL_BG : FRIENDLY_LABEL_EN;
  return map[rawLabel] || rawLabel;
}
function meaningFor(rawLabel, lang) {
  if (!rawLabel) return null;
  const map = lang === "bg" ? MEANING_BG : MEANING_EN;
  return map[rawLabel] || null;
}

// ─── User-friendly "body response" mapping (keyed by backend `primary` string) ───
// Each entry: short emotional label + 1-sentence meaning — for the "Как реагира тялото
// ти в момента" card on the dashboard. Keeps the clinical name as a small subtitle.
const BODY_RESPONSE = {
  metabolically_flexible: {
    icon: "🌱",
    color: "#10B981",
    bg: { label: "В баланс", meaning: "Тялото ти реагира добре на храната — продължи в същия дух, храненето и ритъмът ти работят добре за теб." },
    en: { label: "In balance", meaning: "Your body responds well to food — keep going, your nutrition and rhythm are working for you." },
    es: { label: "En equilibrio", meaning: "Tu cuerpo responde bien a la comida — sigue así, tu nutrición y ritmo están funcionando a tu favor." },
  },
  mild_insulin_resistance: {
    icon: "⚖️",
    color: "#F59E0B",
    bg: { label: "Лека чувствителност към захар", meaning: "Започваш да реагираш по-силно на сладко и тестени храни — малки корекции имат голям ефект сега." },
    en: { label: "Mild sugar sensitivity", meaning: "You're starting to react more strongly to sweets and starchy foods — small changes have a big impact now." },
  },
  insulin_resistance: {
    icon: "🩸",
    color: "#EF4444",
    bg: { label: "По-чувствителен към захар", meaning: "По-лесно огладняваш, особено след хранения с повече въглехидрати, и тялото ти складира повече енергия около корема." },
    en: { label: "More sensitive to sugar", meaning: "You get hungry sooner, especially after carb-heavy meals, and your body stores more energy around the belly." },
  },
  marked_insulin_resistance: {
    icon: "🔥",
    color: "#DC2626",
    bg: { label: "Силно чувствителен към захар", meaning: "Захарта се покачва и задържа по-дълго в кръвта ти — контролът на въглехидратите дава бързи резултати." },
    en: { label: "Highly sensitive to sugar", meaning: "Sugar rises and stays elevated longer in your blood — carb control delivers fast results." },
  },
  diabetic_phenotype: {
    icon: "💉",
    color: "#991B1B",
    bg: { label: "Кръвната захар изисква внимание", meaning: "Тялото ти трудно регулира кръвната захар — проследяване на стойностите и хранене са ключови." },
    en: { label: "Blood sugar needs attention", meaning: "Your body struggles to regulate blood sugar — tracking your values and nutrition are key." },
  },
  thyroid_axis: {
    icon: "🦋",
    color: "#6366F1",
    bg: { label: "Бавен метаболизъм", meaning: "Метаболизмът ти работи по-бавно — затова се чувстваш уморен, често ти е студено и теглото се сваля по-трудно." },
    en: { label: "Slower metabolism", meaning: "Your metabolism is running slower — that's why you feel tired, often cold, and weight loss is harder." },
  },
  hpa_axis_dysregulation: {
    icon: "🧠",
    color: "#8B5CF6",
    bg: { label: "По-чувствителен към захар и стрес", meaning: "По-лесно огладняваш и тялото ти складира повече енергия — редовният сън и по-ниският стрес помагат най-много." },
    en: { label: "More sensitive to sugar and stress", meaning: "You get hungry sooner and your body stores more energy — regular sleep and lower stress help most." },
  },
  sex_hormone_imbalance: {
    icon: "💗",
    color: "#EC4899",
    bg: { label: "Хормоните се колебаят", meaning: "Енергията и апетитът ти се променят през цикъла — хранене според ритъма на тялото дава най-ясни резултати." },
    en: { label: "Hormones are fluctuating", meaning: "Your energy and appetite shift across the cycle — eating with your body's rhythm gives the clearest results." },
  },
  gut_inflammation: {
    icon: "🌿",
    color: "#059669",
    bg: { label: "Червата ти са чувствителни", meaning: "Храносмилателната ти система реагира силно — важно е да откриеш кои храни ти влияят." },
    en: { label: "Your gut is sensitive", meaning: "Your digestive system is reacting strongly — it's important to identify which foods affect you." },
  },
  systemic_inflammation: {
    icon: "🔴",
    color: "#DC2626",
    bg: { label: "Тихо възпаление в тялото", meaning: "Хронично възпаление изтощава енергията ти — противовъзпалителни храни и добър сън могат да помогнат да го намалиш." },
    en: { label: "Quiet inflammation in the body", meaning: "Chronic inflammation drains your energy — anti-inflammatory foods and good sleep can help reduce it." },
  },
};

function bodyResponse(primaryKey, lang) {
  const entry = BODY_RESPONSE[primaryKey];
  if (!entry) return null;
  return {
    icon: entry.icon,
    color: entry.color,
    label: entry[lang]?.label,
    meaning: entry[lang]?.meaning,
  };
}

export default function CompositePhenotypeCard() {
  const { user, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [data, setData] = useState(null);
  const [targets, setTargets] = useState(null);
  const [hero, setHero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSources, setShowSources] = useState(false);

  useEffect(() => {
    // iter460 — Wait for auth to finish bootstrapping before firing requests.
    // Otherwise on a fresh page-load /me/phenotype-composite returns 401 before
    // cookies/sessionStorage have hydrated the Authorization header, and we
    // wrongly fall into the empty-state until the user manually refreshes.
    if (authLoading) return;
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/me/phenotype-composite`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/tracking/targets`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/dashboard/hero`).then((r) => r.data).catch(() => null),
    ]).then(([composite, tgt, hero]) => {
      if (cancelled) return;
      setData(composite);
      setTargets(tgt);
      setHero(hero);
      if (composite) {
        try { sessionStorage.setItem("nutramilo_composite_phenotype_visible", "1"); } catch { /* ignore */ }
      }
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [authLoading, user]);

  if (loading || authLoading) return null;

  const cardCls = isDark
    ? "bg-[hsl(223,47%,11%)] border border-white/5"
    : "bg-white border border-gray-100 shadow-sm";

  // ─── EMPTY STATE — no data yet → onboarding nudge ───
  if (!data || data.fallback) {
    return (
      <div className={`${cardCls} rounded-2xl p-6`} data-testid="composite-phenotype-card-empty">
        <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider opacity-70">
          <span aria-hidden>🧠</span>
          {bg ? "Как реагира тялото ти в момента" : "How your body is responding right now"}
        </div>
        <h3 className={`text-xl font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
          {bg ? "Все още няма достатъчно данни" : "Not enough data yet"}
        </h3>
        <p className={`text-sm mb-5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {bg
            ? "Започни с 3-минутна оценка — ще ти покажем какво ни казват първите ти данни и как да го подобриш."
            : "Start with a 3-minute assessment — we'll show you what your first data says and how to improve it."}
        </p>
        <Link
          to="/assessment"
          data-testid="composite-phenotype-empty-cta"
          className="inline-flex items-center gap-2 px-5 h-10 rounded-full text-sm font-semibold bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white shadow-md hover:opacity-90 transition-opacity"
        >
          {bg ? "Започни 3-минутна оценка" : "Start 3-minute assessment"}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const rawLabel = bg ? data.primary_label_bg : data.primary_label_en;
  const summary = bg ? data.summary_bg : data.summary_en;
  const confidencePct = Math.round((data.confidence || 0) * 100);
  const pCode = targets?.phenotype?.code;          // e.g. "P3"
  const P_NAMES_BG = {
    P1: "В баланс",
    P2: "Чувствителен към захар",
    P3: "Чувствителен към хормонални промени",
    P4: "Възпалителен тип",
    P5: "Хормонален ритъм",
  };
  const P_NAMES_EN = {
    P1: "In balance",
    P2: "Sugar sensitive",
    P3: "Sensitive to hormonal shifts",
    P4: "Inflammatory type",
    P5: "Hormonal rhythm",
  };
  const pLabel = pCode ? (bg ? P_NAMES_BG[pCode] : P_NAMES_EN[pCode]) : null;
  const body = bodyResponse(data.primary, bg ? "bg" : "en");
  // Fall back to legacy friendly/meaning mapping by label text if backend key unknown
  const legacyFriendly = !body ? friendlyLabel(rawLabel, bg ? "bg" : "en") : null;
  const legacyMeaning = !body ? meaningFor(rawLabel, bg ? "bg" : "en") : null;
  const displayLabel = body?.label || legacyFriendly || rawLabel;
  const displayMeaning = body?.meaning || legacyMeaning;
  const displayIcon = body?.icon || data.icon;
  const displayColor = body?.color || data.color;

  return (
    <div className={`${cardCls} rounded-2xl p-4 sm:p-5`} data-testid="composite-phenotype-card">
      {/* iter308 — Always-visible educational-only badge (MDR Rule 11).
          This component shows a clinical-sounding phenotype label, so
          non-clinicians need an unambiguous reminder that this is
          hypothesis-generation, not a diagnosis. */}
      <div
        className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-amber-100 dark:bg-amber-950/50 px-2.5 py-1 text-[10px] font-semibold text-amber-800 dark:text-amber-300 border border-amber-300/60 dark:border-amber-700/40"
        data-testid="composite-phenotype-educational-badge"
        title={bg
          ? "Nutramilo е образователен инструмент, не медицинско изделие (MDR Rule 11)"
          : "Nutramilo is an educational tool, not a medical device (MDR Rule 11)"}
      >
        <span aria-hidden>📚</span>
        <span>
          {bg
            ? "Образователен анализ — не е диагноза"
            : "Educational analysis — not a diagnosis"}
        </span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-70">
          <span aria-hidden>{displayIcon}</span>
          {bg ? "Как реагира тялото ти в момента" : "How your body is responding right now"}
        </div>
        <WhyThisOutput
          model={bg ? "Rule-based composite phenotype engine (DIL)" : "Rule-based composite phenotype engine (DIL)"}
          inputs={bg
            ? ["Симптомна оценка (6 стъпки)", "Лабораторни стойности (ако са качени)", "Тяло: BMI, талия, ханш", "Хранителен дневник (ако е наличен)"]
            : ["Symptom assessment (6 steps)", "Lab values (if uploaded)", "Body: BMI, waist, hip", "Food log (if available)"]}
          method={bg
            ? "Детерминистичен rule-based двигател + educational narrative от GPT-5.2. НЕ е диагноза — educational pattern analysis."
            : "Deterministic rule-based engine + educational narrative from GPT-5.2. NOT a diagnosis — educational pattern analysis."}
          docHref="/legal#ai-methodology"
          dataTestid="composite-phenotype-why-this"
        />
      </div>

      {/* Primary type — emotional label + meaning */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
          style={{ backgroundColor: `${displayColor}20` }}
        >
          <span aria-hidden>{displayIcon}</span>
        </div>
        <div className="flex-1 min-w-0">
          {/* P-type chip intentionally hidden (iter254) — too technical for mass users.
              Keep variable so this stays a single-source-of-truth component.
          {pCode && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="composite-phenotype-p-chips">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
                  isDark ? "bg-[#7B1FA2]/20 text-[#C084FC]" : "bg-[#7B1FA2]/10 text-[#7B1FA2]"
                }`}
                title={bg ? "Метаболитен тип от DIL модела" : "Metabolic type from DIL model"}
              >
                {pCode}
                {pLabel ? ` · ${pLabel}` : ""}
              </span>
            </div>
          )} */}
          <h3
            className="text-lg sm:text-xl font-bold leading-tight inline-flex items-center gap-2"
            style={{ color: displayColor }}
            data-testid="composite-phenotype-primary-label"
          >
            <span aria-hidden className="text-base">⚠️</span>
            {displayLabel}
          </h3>
          {/* iter275: "Какво виждаме" subtitle премахнат — дублираше се с label-а и "Какво означава". */}
        </div>
      </div>

      {/* "Какво означава" — single source of interpretation */}
      {displayMeaning && (
        <div
          className={`mb-3 p-3 rounded-xl ${isDark ? "bg-[hsl(223,47%,14%)] border border-white/5" : "bg-[#F9F9F7] border border-[#E0E0ED]"}`}
          data-testid="composite-phenotype-meaning"
        >
          <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDark ? "text-[#0EA5E9]" : "text-[#0EA5E9]"}`}>
            <span aria-hidden className="mr-1">👉</span>
            {bg ? "Какво означава" : "What this means"}
          </p>
          <p className={`text-sm leading-relaxed ${isDark ? "text-gray-200" : "text-[#1A1A2E]"}`}>
            {displayMeaning}
          </p>
          {/* iter275: backend summary italic блок премахнат — дублираше се с displayMeaning. */}
        </div>
      )}

      {/* iter275: "Какво да направиш днес" — actionable top-1 step от /api/dashboard/hero */}
      {hero?.actions?.[0] && (
        <div
          className={`mb-3 p-3 rounded-xl border-l-4 ${isDark
            ? "bg-gradient-to-br from-emerald-500/10 to-sky-500/10 border-emerald-400"
            : "bg-gradient-to-br from-emerald-50 to-sky-50 border-emerald-500"}`}
          data-testid="composite-phenotype-today-action"
        >
          <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
            <span aria-hidden className="mr-1">✨</span>
            {bg ? "Какво да направиш днес" : "What to do today"}
          </p>
          <p className={`text-sm leading-relaxed font-medium ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? (hero.actions[0].title_bg || hero.actions[0].title_en) : (hero.actions[0].title_en || hero.actions[0].title_bg)}
          </p>
          {(hero.actions[0].description_bg || hero.actions[0].description_en) && (
            <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {bg ? (hero.actions[0].description_bg || hero.actions[0].description_en) : (hero.actions[0].description_en || hero.actions[0].description_bg)}
            </p>
          )}
        </div>
      )}

      {/* Action CTA — iter311: routes to the food diary so the user can
          immediately log a meal aligned with the suggestion. Previously
          pointed to /plan which didn't offer a direct action. */}
      <Link
        to="/dashboard/diary"
        data-testid="composite-phenotype-action-cta"
        className="inline-flex items-center gap-2 px-4 h-9 rounded-full text-sm font-semibold bg-gradient-to-r from-[#10B981] to-[#0EA5E9] text-white shadow-md hover:opacity-90 transition-opacity mb-3"
      >
        {bg ? "Как да подобрите това още днес" : "How to improve this today"}
        <ArrowRight className="w-4 h-4" />
      </Link>

      {/* Confidence bar — simple accuracy meter, без извинителен текст */}
      <div className="flex flex-wrap items-center gap-3 mb-2" data-testid="composite-phenotype-confidence">
        <div className="flex items-center gap-2 text-xs flex-1 min-w-0">
          <Activity className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
          <span className="opacity-70 whitespace-nowrap">
            {bg ? "Точност на анализа" : "Analysis accuracy"}
          </span>
          <div className={`flex-1 h-1.5 rounded-full max-w-[120px] ${isDark ? "bg-white/10" : "bg-gray-200"}`}>
            <div
              className="h-full rounded-full transition-all bg-gradient-to-r from-[#0EA5E9] to-[#10B981]"
              style={{ width: `${Math.max(confidencePct, 8)}%` }}
            />
          </div>
        </div>
      </div>

      {/* iter275: "Добави лабораторни" premested като secondary footer action —
          показва се само когато confidence < 50, с по-дискретен стил. */}
      {confidencePct < 50 && (
        <div className={`mt-3 pt-3 border-t ${isDark ? "border-white/5" : "border-gray-100"}`}>
          <Link
            to="/dashboard/analyses"
            className={`inline-flex items-center gap-1.5 text-[11px] ${isDark ? "text-gray-400 hover:text-[#0EA5E9]" : "text-gray-500 hover:text-[#0EA5E9]"} transition-colors`}
            data-testid="composite-add-labs-cta"
          >
            <span aria-hidden>+</span>
            {bg ? "Добави лабораторни (HOMA-IR, HbA1c, CRP)" : "Add labs (HOMA-IR, HbA1c, CRP)"}
          </Link>
        </div>
      )}

      {/* Secondary types as soft chips */}
      {data.secondary?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          <span className="text-[11px] uppercase tracking-wider opacity-60 mr-1 mt-1">
            {bg ? "Допълнителни сигнали" : "Additional signals"}:
          </span>
          {data.secondary.map((s) => (
            <span
              key={s.key}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px]"
              style={{ backgroundColor: `${s.color}15`, color: s.color }}
            >
              <span aria-hidden>{s.icon}</span>
              {bg ? s.label_bg : s.label_en}
            </span>
          ))}
        </div>
      )}

      {/* Sources expander — soft, non-clinical framing (no "diagnosis", no "disease") */}
      <button
        onClick={() => setShowSources((x) => !x)}
        className={`flex items-center gap-1.5 text-xs transition-colors ${isDark ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}
        data-testid="composite-phenotype-sources-toggle"
      >
        <Info className="w-3.5 h-3.5" />
        {bg ? "Как стигаме до този извод" : "How we reach this conclusion"}
        {showSources ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {showSources && (
        <div className={`mt-3 text-xs pl-1 space-y-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`} data-testid="composite-phenotype-sources-explainer">
          <p className={isDark ? "text-gray-400" : "text-gray-600"}>
            {bg ? "Комбинираме информация от:" : "We combine information from:"}
          </p>
          <ul className="space-y-1">
            <li className="flex items-start gap-2">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#10B981] flex-shrink-0" />
              <span>{bg ? "Храненето ви" : "Your nutrition"}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#0EA5E9] flex-shrink-0" />
              <span>{bg ? "Симптомите, които споделяте" : "The symptoms you share"}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#F59E0B] flex-shrink-0" />
              <span>{bg ? "Лабораторни показатели (ако са налични)" : "Lab values (if available)"}</span>
            </li>
          </ul>
        </div>
      )}

      <AIModuleNotice variant="medical-educational" />
    </div>
  );
}
